import { getCookie } from "@/utils/getCookie";
import { INSTALLMENT_INTEREST_RATE } from "@/utils/installmentInterestRate";

export function getInstallments(price = 0, period) {
  const maxInstallments = 12;
  const results = [];
  const MIN_INSTALLMENT = 500; // R$5.00 in cents

  // Define o número máximo de parcelas baseado no período
  let allowedInstallments = maxInstallments;
  if (period === "quarterly") {
    allowedInstallments = 3;
  } else if (period === "semesterly") {
    allowedInstallments = 6;
  } else if (period === "monthly") {
    allowedInstallments = 1;
  }

  for (let n = 1; n <= allowedInstallments; n++) {
    let installment;

    if (n === 1) {
      // Pagamento único (sem juros)
      installment = Number(price);
    } else {
      // Fórmula de amortização com juros
      installment =
        (price *
          (INSTALLMENT_INTEREST_RATE *
            Math.pow(1 + INSTALLMENT_INTEREST_RATE, n))) /
        (Math.pow(1 + INSTALLMENT_INTEREST_RATE, n) - 1);
    }

    // Verifica se o valor da parcela é pelo menos R$5,00 (500 cents)
    if (installment < MIN_INSTALLMENT) {
      // Se for menor que 500 cents, não faz sentido continuar,
      // pois parcelas subsequentes tendem a ser ainda menores.
      break;
    }

    const periodSuffix =
      period === "weekly"
        ? " / semana"
        : period === "monthly"
        ? " / mês"
        : period === "quarterly"
        ? " / trimestre"
        : period === "semesterly"
        ? " / semestre"
        : period === "yearly"
        ? " / ano"
        : period === "biyearly"
        ? " / a cada 2 anos"
        : "";

    results.push({
      qtt: n,
      value: (installment / 100).toFixed(2),
      total: ((installment * n) / 100).toFixed(2),
      period: periodSuffix,
    });
  }

  if (results.length === 0) {
    return [
      {
        qtt: 1,
        value: (price / 100).toFixed(2),
        total: (price / 100).toFixed(2),
      },
    ];
  }

  return results.reverse();
}

export function formatTaxId(value) {
  // Remove caracteres não numéricos
  value = value.replace(/\D/g, "");

  // Formatação para CPF
  if (value.length <= 11) {
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d)/, "$1.$2");
    value = value.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  // Formatação para CNPJ
  else {
    value = value.replace(/^(\d{2})(\d)/, "$1.$2");
    value = value.replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3");
    value = value.replace(/\.(\d{3})(\d)/, ".$1/$2");
    value = value.replace(/(\d{4})(\d)/, "$1-$2");
  }

  return value;
}

export function formatExpDate(value) {
  // Remove caracteres não numéricos
  value = value.replace(/\D/g, "");

  // Formata para o padrão MM/AA
  if (value.length <= 4) {
    value = value.replace(/(\d{2})(\d{1,2})/, "$1/$2");
  }

  return value;
}

export function formatName(value) {
  // Remove caracteres não alfabéticos
  value = value.replace(/[^a-zA-Z\s]/g, "");

  // Formata a primeira letra de cada palavra para maiúscula
  value = value.replace(/\b\w/g, (char) => char.toUpperCase());
  value = value.replace(/\B\w/g, (char) => char.toLowerCase());

  return value;
}

export function formatPhoneNumber(value) {
  // Remove caracteres não numéricos
  value = value.replace(/\D/g, "");

  // Formatação para telefone com 8 dígitos
  if (value.length <= 10) {
    value = value.replace(/^(\d{2})(\d)/g, "($1) $2");
    value = value.replace(/(\d{4})(\d)/, "$1-$2");
  }
  // Formatação para telefone com 9 dígitos
  else {
    value = value.replace(/^(\d{2})(\d)/g, "($1) $2");
    value = value.replace(/(\d{5})(\d)/, "$1-$2");
  }

  return value;
}

export function isValidEmail(email) {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).toLowerCase());
}

export function formatCreditCardNumber(value) {
  // Remove caracteres não numéricos
  value = value.replace(/\D/g, "");

  // Formata para o padrão 0000 0000 0000 0000
  value = value.replace(/(\d{4})(?=\d)/g, "$1 ");

  return value.trim();
}

export function isValidCreditCardNumber(number) {
  // Adicione sua lógica de validação de número de cartão de crédito aqui
  return number.replace(/\s/g, "").length === 16; // Exemplo: verifica se há 16 dígitos
}

export function getPeriod(offer) {
  switch (offer?.period) {
    case "monthly":
      return "/ mês";
    case "quarterly":
      return "/ trimestre";
    case "semesterly":
      return "/ semestre";
    case "yearly":
      return "/ ano";
    case "biyearly":
      return "/ bianual";
    default:
      return "";
  }
}

export function detectCardType(number) {
  const amexPattern = /^3[47]/;
  return amexPattern.test(number.replace(/\s/g, "")) ? "amex" : "other";
}

export function isValidCPF(cpf) {
  cpf = cpf.replace(/[^\d]/g, "");

  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false;
  }

  const calculateDigit = (base) => {
    let sum = 0;
    for (let i = 0; i < base; i++) {
      sum += parseInt(cpf[i]) * (base + 1 - i);
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const digit1 = calculateDigit(9);
  const digit2 = calculateDigit(10);

  return digit1 === parseInt(cpf[9]) && digit2 === parseInt(cpf[10]);
}

export function isValidCNPJ(cnpj) {
  // Remove caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, "");

  // Verifica se tem 14 dígitos ou se é uma sequência de números iguais
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
    return false;
  }

  // Função auxiliar para calcular o dígito verificador
  const calculateDigit = (base) => {
    const weights =
      base === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    for (let i = 0; i < base; i++) {
      sum += parseInt(cnpj[i]) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  // Calcula os dois dígitos verificadores
  const digit1 = calculateDigit(12);
  const digit2 = calculateDigit(13);

  // Verifica se os dígitos calculados batem com os do CNPJ
  return digit1 === parseInt(cnpj[12]) && digit2 === parseInt(cnpj[13]);
}

export function isValidBrazilianPhone(phone) {
  const validDDDs = [
    11,
    12,
    13,
    14,
    15,
    16,
    17,
    18,
    19, // SP
    21,
    22,
    24, // RJ
    27,
    28, // ES
    31,
    32,
    33,
    34,
    35,
    37,
    38, // MG
    41,
    42,
    43,
    44,
    45,
    46, // PR
    47,
    48,
    49, // SC
    51,
    53,
    54,
    55, // RS
    61, // DF
    62,
    64, // GO
    63, // TO
    65,
    66, // MT
    67, // MS
    68,
    69, // RO, AC
    71,
    73,
    74,
    75,
    77, // BA
    79, // SE
    81,
    82, // PE, AL
    83, // PB
    84, // RN
    85,
    88, // CE
    86,
    87, // Pernambuco
    89, // PI
    91,
    92,
    93,
    94,
    95,
    96,
    97,
    98,
    99, // Norte
  ];

  const phonePattern =
    /^(?:\(?([1-9]{2})\)?\s?)?(?:9\d{4}-?\d{4}|[2-5]\d{3}-?\d{4})$/;

  const match = phone.match(phonePattern);
  if (!match) return false;

  // Extrai o DDD
  const ddd = match[1];
  if (ddd && !validDDDs.includes(Number(ddd))) {
    return false;
  }

  return true;
}

export function isValidName(name) {
  // Remove espaços extras no início e no fim
  const trimmedName = name.trim();

  // Verifica se tem pelo menos um espaço (indica nome + sobrenome)
  if (!trimmedName.includes(" ")) {
    return false;
  }

  // Divide o nome em partes
  const nameParts = trimmedName.split(/\s+/);

  // Verifica se o primeiro e último nomes têm pelo menos 3 letras
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  if (firstName.length < 3 || lastName.length < 3) {
    return false;
  }

  // Regex para validar que só contém letras e espaços
  const namePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/;
  if (!namePattern.test(trimmedName)) {
    return false;
  }

  return true;
}

export function isValidBrazilianCompanyName(companyName) {
  // Remove espaços no início e no fim
  const trimmedCompanyName = companyName.trim();

  // Verifica o comprimento mínimo e máximo
  if (trimmedCompanyName.length < 5 || trimmedCompanyName.length > 150) {
    return false;
  }

  // Regex para validar caracteres permitidos
  const validPattern = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,&/()-]+$/;
  if (!validPattern.test(trimmedCompanyName)) {
    return false;
  }

  // Verifica se não há múltiplos espaços consecutivos
  if (/\s{2,}/.test(trimmedCompanyName)) {
    return false;
  }

  return true;
}

export function getAffiliate(productId) {
  // Try to Get the affiliateId from the URL, if not, try to get from the cookie
  const urlParams = new URLSearchParams(window.location.search);
  const affiliateId = urlParams.get("aff")?.trim();

  if (affiliateId) {
    console.log("Found affiliateId in URL", affiliateId);
    return {
      id: affiliateId,
      where: "url",
    };
  }

  if (!productId || typeof productId !== "string") {
    return null;
  }

  const cookie = getCookie(`aff_${productId}`);

  if (cookie) {
    console.log("Found affiliateId in cookie", cookie);
    return {
      id: cookie,
      where: "cookie",
    };
  }

  return null;
}
